import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React from 'react';

import { DataAPIContext, GET_ONE } from '../api';
import { enabledIntakeAddresses } from './webWidgetContact';

/**
 * Read-only "Website contact form" block on the Web Widget page: the operator's intake
 * address(es) to add to their website's contact form as a Bcc, so form submissions land in
 * the Website inquiries channel. Addresses are created by TourOptima support (backend
 * `web_inquiry_intake add`), so this renders nothing until one exists -- and nothing if the
 * request fails (older backend without the endpoint).
 */
const WebWidgetIntakeAddresses = ({ operatorCode }) => {
  const dataAPI = React.useContext(DataAPIContext);
  const { enqueueSnackbar } = useSnackbar();
  const [addresses, setAddresses] = React.useState([]);

  React.useEffect(() => {
    if (!dataAPI || !operatorCode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await dataAPI(GET_ONE, 'web-chat', {
          urlSuffix: 'intake-addresses/',
          operator_id: operatorCode,
        });
        if (!cancelled && res && res.statusCode === 200) setAddresses(enabledIntakeAddresses(res.data));
      } catch (e) {
        // non-fatal: the section just stays hidden
      }
    })();
    return () => { cancelled = true; };
  }, [dataAPI, operatorCode]);

  if (addresses.length === 0) return null;

  const copy = (address) => {
    navigator.clipboard.writeText(address).then(
      () => enqueueSnackbar('Address copied', { variant: 'success' }),
      () => enqueueSnackbar('Could not copy to clipboard', { variant: 'error' })
    );
  };

  return (
    <Box data-testid="web-widget-intake-addresses">
      <Typography variant="h6" sx={{ mt: 3, mb: 0.5 }}>Website contact form</Typography>
      <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
        Add this address to your website&apos;s contact form as a Bcc (or second recipient).
        Submissions from new visitors arrive in Website inquiries; customers with a booking
        arrive in Main Office as before.
      </Typography>
      {addresses.map((row) => (
        <Box
          key={row.address}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 1.5, py: 0.5,
            borderRadius: 1, bgcolor: 'grey.100', maxWidth: 520,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13, overflowWrap: 'anywhere' }}>
              {row.address}
            </Typography>
            {row.label && (
              <Typography variant="caption" color="text.secondary">{row.label}</Typography>
            )}
          </Box>
          <IconButton size="small" onClick={() => copy(row.address)} aria-label={`copy ${row.address}`}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

WebWidgetIntakeAddresses.propTypes = {
  operatorCode: PropTypes.string,
};

export default WebWidgetIntakeAddresses;
